import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Divider, Progress, Select } from '@mantine/core';
import { Check, CircleCheck, CircleX } from 'tabler-icons-react';
import ResponsiveModal from '../../common/ResponsiveModal';
import AppStack from '../../common/AppStack';
import { Context as AdminContext } from '../../../providers/AdminContextProvider';
import { EXTERNAL_CONNECTION_CONFIGS } from '../external/config/externalConnectionConfigs';
import AppText from '../../common/AppText';
import { triggerNotification } from '../../../helpers/notification';
import AppCard from '../../common/AppCard';
import AppFlexbox from '../../common/AppFlexbox';
import AppCheckbox from '../../common/AppCheckbox';

const AdminProductSyncModal = ({
  isOpen,
  onClose,
  onSuccess,
  ecomExternalProducts
}) => {
  const [formState, setFormState] = useState({
    showConfirm: false,
    fkEcomExternalConnectionType: null,
    syncOptions: [],
    ecomExternalProducts: null,
    progressState: null,
    loading: false
  });
  const { syncEcomExternalProductsWithVendors } = useContext(AdminContext);
  const externalConnectionConfigs = formState.ecomExternalProducts
    ? formState.ecomExternalProducts.reduce((r, c) => {
        const config = EXTERNAL_CONNECTION_CONFIGS.find(
          (f) =>
            f.pkEcomExternalConnectionType.toString() ===
            c.fkEcomExternalConnectionType.toString()
        );
        if (
          config &&
          !r.find(
            (x) =>
              x.pkEcomExternalConnectionType ===
              config.pkEcomExternalConnectionType
          )
        ) {
          r.push(config);
        }
        return r;
      }, [])
    : formState.fkEcomExternalConnectionType
    ? [
        EXTERNAL_CONNECTION_CONFIGS.find(
          (c) =>
            c.pkEcomExternalConnectionType ===
            formState.fkEcomExternalConnectionType
        )
      ]
    : [];
  const syncOptions =
    externalConnectionConfigs?.reduce((r, c) => {
      c.products?.syncOptions
        ?.filter((o) => o.isVendorSync)
        .forEach((e) => {
          if (!r.find((x) => x.dataValue === e.dataValue)) {
            r.push(e);
          }
        });
      return r;
    }, []) || [];

  useEffect(() => {
    if (isOpen) {
      setFormState({
        showConfirm: false,
        fkEcomExternalConnectionType: null,
        syncOptions: [],
        ecomExternalProducts: ecomExternalProducts || null,
        progressState: null,
        loading: false
      });
    }
  }, [isOpen]);

  const onSyncProducts = () => {
    setFormState((c) => ({ ...c, loading: true, progressState: null }));

    const fkEcomExternalConnectionTypes =
      externalConnectionConfigs.length > 0
        ? externalConnectionConfigs.map((c) => c.pkEcomExternalConnectionType)
        : EXTERNAL_CONNECTION_CONFIGS.map(
            (c) => c.pkEcomExternalConnectionType
          );

    const externalProductIds = formState.ecomExternalProducts
      ? formState.ecomExternalProducts.map((p) =>
          p.pkEcomExternalProduct.toString()
        )
      : null;

    syncEcomExternalProductsWithVendors(
      {
        fkEcomExternalConnectionTypes,
        externalProductIds,
        syncOptions: formState.syncOptions
      },
      (progress) => {
        if (progress.completed) {
          triggerNotification('Products synced', 'success');
          setFormState((c) => ({
            ...c,
            loading: false,
            progressState: progress
          }));

          if (onSuccess) {
            onSuccess();
          }
        }
        else if (progress.failed) {
          triggerNotification(progress.failedMessage);
          setFormState((c) => ({
            ...c,
            loading: false,
            progressState: progress
          }));
        }
        else {
          setFormState((c) => ({ ...c, progressState: progress }));
        }
      }
    );
  };

  return (
    <ResponsiveModal
      formSectionProps={
        formState.loading || formState.progressState
          ? {
              isCancelHidden: true,
              isSubmitDisabled: formState.loading,
              submitTitle: 'Close',
              submitColor: 'dark',
              onSubmit: onClose
            }
          : formState.showConfirm
          ? {
              onCancel: () => {
                setFormState({ ...formState, showConfirm: false });
              },
              isLoading: formState.loading,
              onSubmit: onSyncProducts,
              cancelTitle: `Back`,
              submitTitle: `Yes, sync products`
            }
          : {
              onCancel: onClose,
              isLoading: formState.loading,
              onSubmit: () => {
                setFormState({ ...formState, showConfirm: true });
              },
              submitTitle: `Continue`
            }
      }
      isOpen={isOpen}
      noCloseButton={formState.loading}
      onClose={() => {
        if (!formState.loading) {
          onClose();
        }
      }}
      title="Sync products with vendors?"
    >
      <AppStack style={{ gap: 20 }}>
        {formState.loading || formState.progressState ? (
          <AppStack style={{ gap: 8 }}>
            {formState.progressState?.completed ? (
              <AppStack style={{ alignItems: 'center', gap: 8 }}>
                <CircleCheck color="green" size={80} />
                <AppText
                  style={{ fontSize: 16, fontWeight: 500, textAlign: 'center' }}
                >
                  Products synced successfully!
                </AppText>
              </AppStack>
            ) : (
              <>
                {formState.progressState?.failed ? (
                  <AppStack style={{ alignItems: 'center', gap: 8 }}>
                    <CircleX color="red" size={50} />
                    <AppText
                      style={{
                        fontSize: 14,
                        fontWeight: 500,
                        textAlign: 'center',
                        color: '#c40000'
                      }}
                    >
                      Sync failed: {formState.progressState?.failedMessage}
                    </AppText>
                  </AppStack>
                ) : (
                  <AppText
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      textAlign: 'center'
                    }}
                  >
                    Syncing{' '}
                    {formState.ecomExternalProducts
                      ? `(${
                          formState.ecomExternalProducts.length -
                          (formState.progressState?.remainingProductCount ||
                            formState.ecomExternalProducts.length)
                        }/${formState.ecomExternalProducts.length}) `
                      : formState.progressState?.fkEcomExternalConnectionType
                      ? `${
                          EXTERNAL_CONNECTION_CONFIGS.find(
                            (c) =>
                              c.pkEcomExternalConnectionType ===
                              formState.progressState
                                ?.fkEcomExternalConnectionType
                          )?.title
                        } `
                      : ''}
                    products...
                  </AppText>
                )}

                <AppFlexbox style={{ flex: 1 }}>
                  <Progress
                    animated
                    style={{ width: '100%' }}
                    value={formState.progressState?.percent || 0}
                  />
                </AppFlexbox>
              </>
            )}
          </AppStack>
        ) : formState.showConfirm ? (
          <AppText style={{ fontSize: 14 }}>
            Are you sure you want to sync vendor products with{' '}
            <span style={{ fontSize: 14, fontWeight: 500 }}>
              {formState.ecomExternalProducts
                ? formState.ecomExternalProducts.length === 1
                  ? formState.ecomExternalProducts[0].name
                  : `${formState.ecomExternalProducts.length} selected products`
                : `${
                    externalConnectionConfigs[0]?.title ||
                    'all external provider'
                  } products`}
            </span>
            ?
          </AppText>
        ) : (
          <>
            {!ecomExternalProducts && (
              <Select
                clearable
                data={EXTERNAL_CONNECTION_CONFIGS.map((c) => ({
                  label: c.title,
                  value: c.pkEcomExternalConnectionType
                }))}
                label="External provider (optional)"
                onChange={(value) => {
                  setFormState({
                    ...formState,
                    fkEcomExternalConnectionType: value
                  });
                }}
                value={formState.fkEcomExternalConnectionType}
              />
            )}

            <AppStack style={{ gap: 8 }}>
              <AppText
                style={{ fontSize: 14, color: '#666', fontStyle: 'italic' }}
              >
                This will sync the selected products with the latest variant
                data and pricing.
              </AppText>

              <AppText
                style={{ fontSize: 14, color: '#666', fontStyle: 'italic' }}
              >
                Please note that the product title will not be updated.
              </AppText>
            </AppStack>

            {syncOptions.length > 0 && (
              <AppStack style={{ gap: 16 }}>
                <Divider />
                <AppStack style={{ gap: 5 }}>
                  <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                    Extra sync options:
                  </AppText>
                  <AppText
                    style={{
                      fontSize: 14,
                      color: '#666',
                      fontStyle: 'italic'
                    }}
                  >
                    Select the extra options you want to sync:
                  </AppText>
                </AppStack>
                <AppCard
                  radius="md"
                  shadow="none"
                  style={{ padding: 0 }}
                  withBorder
                >
                  <AppStack style={{ gap: 0 }}>
                    {syncOptions.map((option, index) => {
                      const selected = formState.syncOptions.includes(
                        option.dataValue
                      );

                      return (
                        <React.Fragment key={option.dataValue}>
                          {index > 0 && <Divider />}
                          <AppFlexbox
                            onClick={() => {
                              setFormState({
                                ...formState,
                                syncOptions: selected
                                  ? formState.syncOptions.filter(
                                      (s) => s !== option.dataValue
                                    )
                                  : [...formState.syncOptions, option.dataValue]
                              });
                            }}
                            style={{ padding: 16, cursor: 'pointer' }}
                          >
                            <AppCheckbox
                              key={option.dataValue}
                              checked={selected}
                              description={option.description}
                              disabled={formState.loading}
                              label={option.label}
                              onChange={() => {}}
                              style={{
                                fontWeight: 500,
                                pointerEvents: 'none'
                              }}
                            />
                          </AppFlexbox>
                        </React.Fragment>
                      );
                    })}
                  </AppStack>
                </AppCard>
              </AppStack>
            )}
          </>
        )}
      </AppStack>
    </ResponsiveModal>
  );
};

AdminProductSyncModal.propTypes = {
  ecomExternalProducts: PropTypes.array,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func
};

export default AdminProductSyncModal;
